import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "lib/utils";

const trustPanelVariants = cva("h-fit w-full", {
    variants: {
        variant: {
            soft: "rounded-2xl border border-trust-border bg-gradient-to-b from-trust-bg-start to-trust-bg-end px-6 py-6 shadow-none sm:px-7 sm:py-7",
            outline: "rounded-2xl border border-trust-border bg-trust-bg-end px-6 py-6 shadow-sm sm:px-7 sm:py-7",
            muted: "rounded-2xl border border-trust-border/80 bg-trust-bg-start px-6 py-6 sm:px-7 sm:py-7",
            elevated:
                "rounded-2xl border border-trust-border bg-gradient-to-b from-trust-bg-start to-trust-bg-end px-6 py-6 shadow-md sm:px-7 sm:py-7",
        },
    },
    defaultVariants: {
        variant: "soft",
    },
});

const trustPanelTitleVariants = cva("font-bold tracking-tight text-trust-heading", {
    variants: {
        size: {
            default: "text-lg sm:text-xl",
            sm: "text-base sm:text-lg",
            lg: "text-xl sm:text-2xl",
        },
    },
    defaultVariants: {
        size: "default",
    },
});

const trustPanelListVariants = cva("flex flex-col", {
    variants: {
        density: {
            default: "mt-6 gap-6",
            compact: "mt-4 gap-4",
            relaxed: "mt-8 gap-8",
        },
    },
    defaultVariants: {
        density: "default",
    },
});

const trustIconVariants = cva(
    "flex shrink-0 items-center justify-center rounded-xl text-white [&_svg]:pointer-events-none",
    {
        variants: {
            palette: {
                brand: "bg-brand-accent",
                trust: "bg-trust-accent",
                primary: "bg-primary",
                violet: "bg-violet-600",
                emerald: "bg-emerald-600",
            },
            size: {
                md: "h-12 w-12 [&_svg]:size-5",
                sm: "h-10 w-10 [&_svg]:size-4",
                lg: "h-14 w-14 [&_svg]:size-6",
            },
        },
        defaultVariants: {
            palette: "brand",
            size: "md",
        },
    },
);

const trustPanelItemVariants = cva("flex min-w-0", {
    variants: {
        gap: {
            default: "gap-4",
            compact: "gap-3",
        },
    },
    defaultVariants: {
        gap: "default",
    },
});

const trustPanelItemTitleVariants = cva("font-bold text-trust-heading", {
    variants: {
        size: {
            default: "text-sm sm:text-base",
            sm: "text-sm",
        },
    },
    defaultVariants: {
        size: "default",
    },
});

const trustPanelItemDescriptionVariants = cva("text-trust-body", {
    variants: {
        size: {
            default: "text-sm",
            sm: "text-xs sm:text-sm",
        },
    },
    defaultVariants: {
        size: "default",
    },
});

export interface TrustPanelProps
    extends React.HTMLAttributes<HTMLDivElement>, VariantProps<typeof trustPanelVariants> {}

export interface TrustPanelTitleProps
    extends React.HTMLAttributes<HTMLHeadingElement>, VariantProps<typeof trustPanelTitleVariants> {}

export interface TrustPanelListProps
    extends React.HTMLAttributes<HTMLDivElement>, VariantProps<typeof trustPanelListVariants> {}

export interface TrustIconProps extends React.HTMLAttributes<HTMLDivElement>, VariantProps<typeof trustIconVariants> {}

export interface TrustPanelItemProps
    extends React.HTMLAttributes<HTMLDivElement>, VariantProps<typeof trustPanelItemVariants> {}

export interface TrustPanelItemTitleProps
    extends React.HTMLAttributes<HTMLParagraphElement>, VariantProps<typeof trustPanelItemTitleVariants> {}

export interface TrustPanelItemDescriptionProps
    extends React.HTMLAttributes<HTMLParagraphElement>, VariantProps<typeof trustPanelItemDescriptionVariants> {}

const TrustPanel = React.forwardRef<HTMLDivElement, TrustPanelProps>(({ className, variant, ...props }, ref) => (
    <div ref={ref} className={cn(trustPanelVariants({ variant }), className)} {...props} />
));
TrustPanel.displayName = "TrustPanel";

const TrustPanelTitle = React.forwardRef<HTMLHeadingElement, TrustPanelTitleProps>(
    ({ className, size, ...props }, ref) => (
        <h2 ref={ref} className={cn(trustPanelTitleVariants({ size }), className)} {...props} />
    ),
);
TrustPanelTitle.displayName = "TrustPanelTitle";

const TrustPanelList = React.forwardRef<HTMLDivElement, TrustPanelListProps>(
    ({ className, density, ...props }, ref) => (
        <div ref={ref} className={cn(trustPanelListVariants({ density }), className)} {...props} />
    ),
);
TrustPanelList.displayName = "TrustPanelList";

const TrustIcon = React.forwardRef<HTMLDivElement, TrustIconProps>(
    ({ className, palette, size, children, ...props }, ref) => (
        <div ref={ref} className={cn(trustIconVariants({ palette, size }), className)} {...props}>
            {children}
        </div>
    ),
);
TrustIcon.displayName = "TrustIcon";

const TrustPanelItem = React.forwardRef<HTMLDivElement, TrustPanelItemProps>(({ className, gap, ...props }, ref) => (
    <div ref={ref} className={cn(trustPanelItemVariants({ gap }), className)} {...props} />
));
TrustPanelItem.displayName = "TrustPanelItem";

const TrustPanelItemTitle = React.forwardRef<HTMLParagraphElement, TrustPanelItemTitleProps>(
    ({ className, size, ...props }, ref) => (
        <p ref={ref} className={cn(trustPanelItemTitleVariants({ size }), className)} {...props} />
    ),
);
TrustPanelItemTitle.displayName = "TrustPanelItemTitle";

const TrustPanelItemDescription = React.forwardRef<HTMLParagraphElement, TrustPanelItemDescriptionProps>(
    ({ className, size, ...props }, ref) => (
        <p ref={ref} className={cn(trustPanelItemDescriptionVariants({ size }), className)} {...props} />
    ),
);
TrustPanelItemDescription.displayName = "TrustPanelItemDescription";

export {
    TrustPanel,
    TrustPanelTitle,
    TrustPanelList,
    TrustIcon,
    TrustPanelItem,
    TrustPanelItemTitle,
    TrustPanelItemDescription,
    trustPanelVariants,
    trustIconVariants,
};
